/**
 * Aba "Nível" da barra lateral.
 *
 * O painel responde, num relance, à pergunta que a camada existe para
 * responder: **a bacia está seca ou cheia para esta época?** — contando
 * quantas estações estão abaixo, dentro e acima do que os anos medidos
 * mostram.
 */
import { CircleQuestionMark, ExternalLink, RefreshCw, Settings2, Waves } from 'lucide-react';
import { useMemo, useState } from 'react';
import { formatInt } from '../lib/format';
import { formatRelative } from '../lib/time';
import { Badge, Button, Dialog, Hint, IconButton, Section, Slider, Toggle } from '../ui/primitives';
import { avisoDeFrescor } from './frescor';
import { CLASSES, COR_CLASSE, COR_SEM_REFERENCIA } from './look';
import { NOME_CLASSE } from './nivel';
import { LIMITES, useRiverStore } from './store';
import { carregarRios } from './sync';
import type { Classe } from './types';

function Legenda({ contagem }: { contagem: Map<Classe | 'semRef', number> }) {
  return (
    <div className="space-y-2">
      <div>
        <div className="flex gap-0.5">
          {CLASSES.map((c) => (
            <div key={c} className="flex-1" title={NOME_CLASSE[c]}>
              <div className="h-2.5 rounded-[2px]" style={{ background: COR_CLASSE[c] }} />
              <div className="mt-0.5 text-center text-[9px] text-ink-400 tabular-nums">{contagem.get(c) ?? 0}</div>
            </div>
          ))}
        </div>
        <div className="mt-0.5 flex justify-between text-[9px] text-ink-400">
          <span>seca para a época</span>
          <span>cheia para a época</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-ink-300">
        <span className="inline-flex items-center gap-1">
          <svg viewBox="-6 -6 12 12" width={13} height={13} aria-hidden className="shrink-0">
            <circle cx="0" cy="0" r="4.2" fill="none" stroke={COR_SEM_REFERENCIA} strokeWidth="1.3" strokeDasharray="2.5 2" />
          </svg>
          sem referência histórica
        </span>
        <span className="inline-flex items-center gap-1 text-rio-300">▲ subindo</span>
        <span className="inline-flex items-center gap-1 text-fogo-400">▼ descendo</span>
        <span className="inline-flex items-center gap-1 text-ink-300">– estável</span>
      </div>
    </div>
  );
}

function AparenciaDialog(props: { open: boolean; onClose: () => void }) {
  const estilo = useRiverStore((s) => s.estilo);
  const setEstilo = useRiverStore((s) => s.setEstilo);
  return (
    <Dialog title="Aparência do nível dos rios" open={props.open} onClose={props.onClose}>
      <div className="space-y-4 p-4">
        <Slider label="Tamanho" value={estilo.tamanho} {...LIMITES.tamanho} onChange={(tamanho) => setEstilo({ tamanho })} />
        <Slider
          label="Opacidade"
          value={estilo.opacidade}
          {...LIMITES.opacidade}
          onChange={(opacidade) => setEstilo({ opacidade })}
        />
        <Toggle
          label="Mostrar a seta de tendência"
          checked={estilo.mostrarTendencia}
          onChange={(mostrarTendencia) => setEstilo({ mostrarTendencia })}
          description="▲ ▼ ao lado do ponto, pela variação das últimas 24 h."
        />
        <Toggle
          label="Mostrar estações sem referência histórica"
          checked={estilo.mostrarSemReferencia}
          onChange={(mostrarSemReferencia) => setEstilo({ mostrarSemReferencia })}
          description="Círculo vazado e tracejado: a cota está medida, mas não há anos suficientes para dizer o que é normal ali."
        />
      </div>
    </Dialog>
  );
}

export function RiverPanel() {
  const visivel = useRiverStore((s) => s.visivel);
  const situacao = useRiverStore((s) => s.situacao);
  const dados = useRiverStore((s) => s.dados);
  const setVisivel = useRiverStore((s) => s.setVisivel);
  const [aparenciaAberta, setAparenciaAberta] = useState(false);

  const aviso = avisoDeFrescor(dados.gerado);

  const contagem = useMemo(() => {
    const m = new Map<Classe | 'semRef', number>();
    for (const r of dados.rios) m.set(r.classe ?? 'semRef', (m.get(r.classe ?? 'semRef') ?? 0) + 1);
    return m;
  }, [dados.rios]);

  const abaixo = (contagem.get('muitoAbaixo') ?? 0) + (contagem.get('abaixo') ?? 0);
  const acima = (contagem.get('acima') ?? 0) + (contagem.get('muitoAcima') ?? 0);
  const dentro = contagem.get('normal') ?? 0;
  const semRef = contagem.get('semRef') ?? 0;

  return (
    <div>
      <Section
        title="Nível dos rios"
        icon={Waves}
        right={
          visivel && (
            <IconButton
              icon={Settings2}
              size="sm"
              label="Aparência do nível dos rios"
              onClick={() => setAparenciaAberta(true)}
              className="pointer-coarse:size-11"
            />
          )
        }
      >
        <Toggle
          label="Cota das estações da ANA"
          checked={visivel}
          onChange={setVisivel}
          description="Régua de cada estação telemétrica, comparada com o que os anos medidos mostram para esta época."
        />

        {visivel && aviso && (
          <div className="mt-2 rounded-lg border border-alerta-400/40 bg-alerta-400/10 px-2.5 py-1.5 text-[11px] leading-snug text-alerta-200">
            {aviso}
          </div>
        )}

        {visivel && dados.rios.length > 0 && (
          <>
            <div className="flex flex-wrap items-center gap-1.5 pt-2.5">
              {abaixo > 0 && <Badge tone="fogo">{formatInt(abaixo)} abaixo do normal</Badge>}
              {dentro > 0 && <Badge>{formatInt(dentro)} dentro do normal</Badge>}
              {acima > 0 && <Badge tone="rio">{formatInt(acima)} acima do normal</Badge>}
              {semRef > 0 && <Badge>{formatInt(semRef)} sem referência</Badge>}
            </div>

            <Hint>
              A cor é a posição da cota de hoje entre os anos medidos para esta época — não o número da régua. Cota de
              estações diferentes não se compara: cada régua tem o seu próprio zero.
            </Hint>

            <div className="pt-3">
              <Legenda contagem={contagem} />
            </div>

            <Hint>Clique numa estação para ver a série de 48 h e a comparação com os anos medidos.</Hint>
          </>
        )}
      </Section>

      <Section title="Dados" icon={RefreshCw} defaultOpen={false}>
        <div className="space-y-1.5 text-xs text-ink-300">
          {situacao.estado === 'loading' && <div>Baixando…</div>}
          {situacao.estado === 'error' && <div className="text-alerta-300">Falha ao baixar: {situacao.erro}</div>}
          {situacao.mensagem && <div className="text-alerta-300">{situacao.mensagem}</div>}
          {situacao.estado === 'ready' && (
            <div>
              {formatInt(dados.rios.length)} estações
              {dados.gerado !== null && <> · atualizado {formatRelative(dados.gerado)}</>}
            </div>
          )}
          <Button icon={RefreshCw} onClick={() => void carregarRios(true)}>
            Atualizar agora
          </Button>
        </div>
      </Section>

      <Section title="O que é" icon={CircleQuestionMark} defaultOpen={false}>
        <div className="space-y-2 text-xs leading-relaxed text-ink-300">
          <p>
            Na Amazônia o rio é o calendário: a cheia e a seca definem quando o barco sai, se a balsa de combustível
            chega e se a comunidade fica isolada. A ANA mantém uma rede de réguas telemétricas que medem a{' '}
            <strong>cota</strong> — a altura da água na régua daquela estação — de 15 em 15 minutos.
          </p>
          <p>
            O número da régua sozinho engana: cada estação tem o seu próprio zero, então{' '}
            <strong>cota de rios diferentes não se compara</strong>. Por isso a cor no mapa não é a cota, e sim onde a
            medição de hoje cai entre os anos medidos <em>para esta mesma época do ano</em>.
          </p>
          <p>
            A comparação usa uma janela de quinze dias em torno da data, e só aparece nas estações com pelo menos oito
            anos medidos. As outras ficam com o círculo vazado: a cota está lá, a referência não.
          </p>
          <p className="text-ink-400">
            Fonte: Agência Nacional de Águas e Saneamento Básico — rede telemétrica do SNIRH.{' '}
            <a
              href="https://www.snirh.gov.br/hidroweb/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 text-rio-300 hover:underline"
            >
              snirh.gov.br/hidroweb
              <ExternalLink size={10} />
            </a>
          </p>
          <p className="text-ink-400">Não serve para navegação nem para alerta de cheia.</p>
        </div>
      </Section>

      <AparenciaDialog open={aparenciaAberta} onClose={() => setAparenciaAberta(false)} />
    </div>
  );
}
